import { Autocomplete, Box, Checkbox, Grid, Stack, TextField, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import React, { useEffect, useState, useMemo } from 'react';
import { BackButton, Button, ErrorMessage, Heading, Input, Select, UploadImage } from '../../components/Common';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAddNewEmployeeMutation, useGetEmployeeDetailQuery, useUpdateEmployeeMutation } from '../../services/Employee';
import { bloodGroupList, countryList, userTypeList } from '../../helper';
import { useMessage } from '../../components/Common/Message/MessageContext';
import { useGetserviceDepartmentDropdownQuery } from '../../services/ServiceDepartment';
import { useGetCityListQuery, useGetStateListQuery } from '../../services/Location';
import { useGetPincodeByCityQuery, useGetExtraPincodeByCityQuery } from '../../services/SubAdmin';
import { getAllData } from '../../helper/indexDB';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Maximum file size in bytes (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const AddEmployees = () => {
    const { showMessage } = useMessage();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [profileImage, setProfileImage] = useState([]);
    const [userType, setUserType] = useState('');
    const [user, setUser] = useState('');
    const [country, setCountry] = useState(1);
    const [states, setStates] = useState('');
    const [city, setCity] = useState('');
    const [aadharImage, setAadharImage] = useState([]);
    const [bloodGroup, setBloodGroup] = useState('');
    const [pincodes, setPincodes] = useState([]);
    const [adminRole, setAdminRole] = useState('');

    const [formValues, setFormValues] = useState({
        name: '',
        mobile: '',
        email: '',
        guardianName: '',
        guardianMobile: '',
        address: '',
        panNo: '',
        dob: '',
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [profileErrorMessage, setProfileErrorMessage] = useState('');
    const [addharErrorMessage, setAddharErrorMessage] = useState('');

    const { data, error, isLoading } = useGetserviceDepartmentDropdownQuery();
    const serviceList = data?.data;
    const { data: cityLst, error: cityError, isLoading: cityLoading } = useGetCityListQuery(states, { skip: !states });
    const cityList = cityLst?.data;
    const { data: stateLst, error: stateError, isLoading: stateLoading } = useGetStateListQuery();
    const stateList = stateLst?.data;
    
    // FIXED: Use city-based pincode queries instead of user-based
    const { data: extraPincodeData, isLoading: extraPincodeLoading } = useGetExtraPincodeByCityQuery(city, {
        skip: !city,
        refetchOnMountOrArgChange: true,
    });

    const { data: cityPincodeOwnershipData, isLoading: cityPincodeLoading } = useGetPincodeByCityQuery(city, {
        skip: !city,
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
    });

    // Map pincode -> adminId from backend ownership data
    const pincodeOwnershipMap = useMemo(() => {
        const map = {};
        const list = cityPincodeOwnershipData?.data || cityPincodeOwnershipData?.pinCodeData || [];
        list.forEach((item) => {
            const pin = item.pincode || item.pinCode;
            if (pin) {
                map[pin] = item.adminId || null;
            }
        });
        return map;
    }, [cityPincodeOwnershipData]);

    // All pincodes in selected city
    const pincodeList = useMemo(() => {
        if (!city) return [];
        
        const extraPins = extraPincodeData?.data || [];
        
        return extraPins.map((pin) => ({
            pincode: pin.pincode,
            adminId: pincodeOwnershipMap[pin.pincode] ?? null,
        }));
    }, [city, extraPincodeData, pincodeOwnershipMap]);

    const [addEmployee] = useAddNewEmployeeMutation();
    const [updateEmployee] = useUpdateEmployeeMutation();
    const { data: employeeLst } = useGetEmployeeDetailQuery(state?.id, { skip: !state, refetchOnMountOrArgChange: true });
    const employeeDetails = employeeLst?.data;

    useEffect(() => {
        const fetchData = async () => {
            const storedData = await getAllData();
            setUser(storedData?.[0]?.value.id);
            setAdminRole(storedData?.[0]?.value.type);

            if (storedData?.[0]?.value.type !== 1) {
                setCity(storedData?.[0]?.value.city || '');
                setStates(storedData?.[0]?.value.state || '');
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (employeeDetails && state) {
            setFormValues({
                name: employeeDetails?.name || '',
                mobile: employeeDetails?.mobile || '',
                email: employeeDetails?.email || '',
                guardianName: employeeDetails?.guardianName || '',
                guardianMobile: employeeDetails?.guardianMobile || '',
                address: employeeDetails?.address || '',
                panNo: employeeDetails?.panNo || '',
                dob: employeeDetails?.dob ? new Date(employeeDetails.dob).toISOString().split('T')[0] : '',
                id: state.id,
            });
            setProfileImage(employeeDetails?.profileImage || []);
            setAadharImage(employeeDetails?.aadharImage || []);
            setBloodGroup(employeeDetails?.bloodGroup || '');
            setStates(employeeDetails?.state || '');
            setCity(employeeDetails?.city || '');
            setUserType(employeeDetails?.workType?.id || '');
            setPincodes(employeeDetails?.pinCode || []);
        }
    }, [employeeDetails, state]);

    // FIXED: Clear pincodes when city changes
    useEffect(() => {
        if (!city || !pincodeList.length) {
            setPincodes([]);
            return;
        }

        // In edit mode, keep existing pincodes if they're valid for this city
        if (state?.id && employeeDetails?.pinCode) {
            const validPincodes = employeeDetails.pinCode.filter((pin) =>
                pincodeList.some((cp) => cp.pincode === pin)
            );
            setPincodes(validPincodes);
        } else {
            // In add mode, clear pincodes when city changes
            setPincodes([]);
        }
    }, [city, pincodeList, state?.id]);

    const handleUserTypeChange = (e) => setUserType(e.target.value);
    const handleStateChange = (e) => {
        setStates(e.target.value);
        setCity(''); // Reset city when state changes
        setPincodes([]); // Clear pincodes when state changes
    };
    const handleCountryChange = (e) => setCountry(e.target.value);
    const handleCityChange = (e) => {
        setCity(e.target.value);
        setPincodes([]); // Clear pincodes when city changes
        setErrorMessages(prev => ({ ...prev, pincodes: '' }));
    };
    const handleBloodGroupChange = (e) => setBloodGroup(e.target.value);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
        // Clear error for this field
        if (errorMessages[name]) {
            setErrorMessages(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateField = (field, fieldName) => {
        if (!field || field === '') {
            setErrorMessages(prevState => ({ ...prevState, [fieldName]: `${fieldName} is a mandatory field.` }));
            return true;
        }
        setErrorMessages(prevState => ({ ...prevState, [fieldName]: '' }));
        return false;
    };

    // Validate file size
    const validateFileSize = (file) => {
        if (file && file.size > MAX_FILE_SIZE) {
            return false;
        }
        return true;
    };

    // Compress image before upload
    const compressImage = (file, maxSizeMB = 1) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    
                    // Calculate new dimensions (max 1920px width)
                    let width = img.width;
                    let height = img.height;
                    const maxWidth = 1920;
                    
                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }
                    
                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);
                    
                    // Start with quality 0.8 and reduce if needed
                    let quality = 0.8;
                    canvas.toBlob((blob) => {
                        if (blob.size > maxSizeMB * 1024 * 1024 && quality > 0.5) {
                            quality -= 0.1;
                            canvas.toBlob((newBlob) => {
                                resolve(new File([newBlob], file.name, { type: 'image/jpeg' }));
                            }, 'image/jpeg', quality);
                        } else {
                            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
                        }
                    }, 'image/jpeg', quality);
                };
            };
            reader.onerror = reject;
        });
    };

    const handleFormSubmit = async () => {
        setErrorMessages({});
        setProfileErrorMessage('');
        setAddharErrorMessage('');

        let hasError = false;

        // Validate profile image
        if (!profileImage || profileImage.length === 0) {
            setProfileErrorMessage('Please select a profile image to upload.');
            hasError = true;
        }

        // Validate Aadhar image
        if (!aadharImage || aadharImage.length === 0) {
            setAddharErrorMessage('Please select an Aadhar image to upload.');
            hasError = true;
        }

        // Validate pincodes
        if (pincodes.length === 0) {
            setErrorMessages(prev => ({ ...prev, pincodes: 'Please select at least one pincode.' }));
            hasError = true;
        }

        // Validate all fields
        const nameError = validateField(formValues.name, 'name');
        const mobileError = validateField(formValues.mobile, 'mobile');
        const emailError = validateField(formValues.email, 'email');
        const guardianNameError = validateField(formValues.guardianName, 'guardianName');
        const guardianMobileError = validateField(formValues.guardianMobile, 'guardianMobile');
        const countryError = validateField(country, 'country');
        const stateError = validateField(states, 'state');
        const cityError = validateField(city, 'city');
        const addressError = validateField(formValues.address, 'address');
        const panNoError = validateField(formValues.panNo, 'panNo');
        const dobError = validateField(formValues.dob, 'dob');
        const userTypeError = validateField(userType, 'userType');
        const bloodGroupError = validateField(bloodGroup, 'bloodGroup');

        if (
            nameError || mobileError || emailError || guardianNameError || guardianMobileError ||
            countryError || stateError || cityError || addressError || hasError ||
            panNoError || dobError || userTypeError || bloodGroupError
        ) {
            showMessage('error', 'Please fill all mandatory fields');
            return;
        }

        try {
            const formData = new FormData();
            
            // Handle profile image compression
            if (profileImage && profileImage instanceof File) {
                const compressedProfile = await compressImage(profileImage, 1);
                formData.append('profileImage', compressedProfile);
            } else if (typeof profileImage === 'string') {
                // In edit mode, if profileImage is a URL string, don't append it
                // The backend will keep the existing image
            }

            // Handle Aadhar image compression
            if (aadharImage && aadharImage instanceof File) {
                const compressedAadhar = await compressImage(aadharImage, 1);
                formData.append('aadharImage', compressedAadhar);
            } else if (typeof aadharImage === 'string') {
                // In edit mode, if aadharImage is a URL string, don't append it
            }

            formData.append('workType', userType);
            formData.append('bloodGroup', bloodGroup);
            formData.append('name', formValues.name);
            formData.append('mobile', formValues.mobile);
            formData.append('email', formValues.email);
            formData.append('guardianName', formValues.guardianName);
            formData.append('guardianMobile', formValues.guardianMobile);
            formData.append('country', country);
            formData.append('state', states);
            formData.append('city', city);
            formData.append('address', formValues.address);
            formData.append('pincode', JSON.stringify(pincodes));
            formData.append('panNo', formValues.panNo);
            formData.append('dob', formValues.dob);

            let response;

            if (state) {
                response = await updateEmployee({ formData, id: state.id });
            } else {
                response = await addEmployee(formData);
            }

            const { status, message } = response?.data || {};
            if (status) {
                navigate('/empolyees');
                showMessage('success', message);
            } else {
                showMessage('error', message || 'Failed to save employee');
            }
        } catch (error) {
            console.error('Failed to save the employee:', error);
            showMessage('error', 'An error occurred while saving the employee');
        }
    };

    return (
        <>
            <Stack flexDirection={'row'} gap={1} mb={1}>
                <BackButton />
                <Heading head={state ? 'Edit Employee' : 'Add Employee'} />
            </Stack>
            <Box className='card_container'>
                <Grid container spacing={{ xs: 1, sm: 2 }}>
                    <Grid item xs={12}>
                        <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>
                            User Photo (Max 5MB)
                        </Typography>
                        <UploadImage files={profileImage} setFiles={setProfileImage} />
                        <ErrorMessage message={profileErrorMessage} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Select 
                            selectList={isLoading ? [] : serviceList} 
                            labelinput='User Type' 
                            name="workType" 
                            value={userType} 
                            onChange={handleUserTypeChange} 
                        />
                        {errorMessages.userType && <ErrorMessage message={errorMessages.userType} />}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Input 
                            labelinput='Technician Name' 
                            name="name" 
                            value={formValues.name} 
                            onChange={handleInputChange} 
                        />
                        {errorMessages.name && <ErrorMessage message={errorMessages.name} />}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Input 
                            labelinput='Technician Mobile' 
                            type='number' 
                            name="mobile" 
                            value={formValues.mobile} 
                            onChange={handleInputChange} 
                        />
                        {errorMessages.mobile && <ErrorMessage message={errorMessages.mobile} />}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Input 
                            labelinput='Email' 
                            type='email' 
                            name="email" 
                            value={formValues.email} 
                            onChange={handleInputChange} 
                        />
                        {errorMessages.email && <ErrorMessage message={errorMessages.email} />}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Input 
                            labelinput='Guardian Name' 
                            name="guardianName" 
                            value={formValues.guardianName} 
                            onChange={handleInputChange} 
                        />
                        {errorMessages.guardianName && <ErrorMessage message={errorMessages.guardianName} />}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Input 
                            labelinput='Guardian Mobile' 
                            name="guardianMobile" 
                            value={formValues.guardianMobile} 
                            onChange={handleInputChange} 
                        />
                        {errorMessages.guardianMobile && <ErrorMessage message={errorMessages.guardianMobile} />}
                    </Grid>
                    <Grid item xs={12}>
                        <Input 
                            labelinput='Address' 
                            name="address" 
                            value={formValues.address} 
                            onChange={handleInputChange} 
                        />
                        {errorMessages.address && <ErrorMessage message={errorMessages.address} />}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Select 
                            selectList={countryList} 
                            labelinput='Country' 
                            name="country" 
                            value={country} 
                            onChange={handleCountryChange} 
                        />
                        {errorMessages.country && <ErrorMessage message={errorMessages.country} />}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Select 
                            selectList={stateLoading ? [] : stateList} 
                            disabled={adminRole !== 1} 
                            labelinput='State' 
                            name="state" 
                            value={states} 
                            onChange={handleStateChange} 
                        />
                        {errorMessages.state && <ErrorMessage message={errorMessages.state} />}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Select 
                            selectList={cityLoading ? [] : cityList} 
                            disabled={adminRole !== 1} 
                            labelinput='City' 
                            name="city" 
                            value={city} 
                            onChange={handleCityChange} 
                        />
                        {errorMessages.city && <ErrorMessage message={errorMessages.city} />}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                        <Input 
                            labelinput='Pan Card No' 
                            name="panNo" 
                            value={formValues.panNo} 
                            onChange={handleInputChange} 
                        />
                        {errorMessages.panNo && <ErrorMessage message={errorMessages.panNo} />}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>
                            Select Pincodes
                        </Typography>
                        <Autocomplete
                            multiple
                            id="pincode-select"
                            options={pincodeList}
                            loading={extraPincodeLoading || cityPincodeLoading}
                            disableCloseOnSelect
                            getOptionLabel={(option) => {
                                if (typeof option === 'object') return option.pincode || '';
                                return option || '';
                            }}
                            isOptionEqualToValue={(option, value) => {
                                const optionValue = typeof option === 'object' ? option.pincode : option;
                                const valueValue = typeof value === 'object' ? value.pincode : value;
                                return optionValue === valueValue;
                            }}
                            value={pincodeList.filter((p) => pincodes.includes(p.pincode))}
                            onChange={(event, newValue) => {
                                const formattedPincodes = newValue.map(item =>
                                    typeof item === 'object' ? item.pincode : item
                                );
                                setPincodes(formattedPincodes);
                                setErrorMessages(prev => ({ ...prev, pincodes: '' }));
                            }}
                            renderOption={(props, option, { selected }) => {
                                const { key, ...otherProps } = props;
                                const pincodeValue = typeof option === 'object' ? option.pincode : option;
                                return (
                                    <li key={key} {...otherProps}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {pincodeValue}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select pincodes"
                                />
                            )}
                        />
                        {errorMessages.pincodes && <ErrorMessage message={errorMessages.pincodes} />}
                    </Grid>
                    <Grid item xs={12}>
                        <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>
                            Upload Aadhar Card (Max 5MB)
                        </Typography>
                        <UploadImage files={aadharImage} setFiles={setAadharImage} />
                        <ErrorMessage message={addharErrorMessage} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Select 
                            selectList={bloodGroupList} 
                            labelinput='Blood Group' 
                            name="bloodGroup" 
                            value={bloodGroup} 
                            onChange={handleBloodGroupChange} 
                        />
                        {errorMessages.bloodGroup && <ErrorMessage message={errorMessages.bloodGroup} />}
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Input 
                            labelinput='User DOB' 
                            name="dob" 
                            type='date' 
                            value={formValues.dob} 
                            onChange={handleInputChange} 
                        />
                        {errorMessages.dob && <ErrorMessage message={errorMessages.dob} />}
                    </Grid>
                    <Grid item xs={12}>
                        <Stack flexDirection={'row'} gap={1}>
                            <Button buttonName='save' onClick={handleFormSubmit} />
                            <Button buttonName='cancel' color='white' onClick={() => navigate('/empolyees')} />
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default AddEmployees;